import React, { useState } from 'react'
import {
  Box,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  InputAdornment
} from '@mui/material'
import WorkIcon from '@mui/icons-material/Work'
import EditIcon from '@mui/icons-material/Edit'
import SearchIcon from '@mui/icons-material/Search'
import FilterListIcon from '@mui/icons-material/FilterList'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import JobEditModal from './JobEditModal'

const JobReviewStep = ({ 
  jobs, 
  selectedJobs, 
  onJobUpdate, 
  onJobSelection, 
  onBulkSelection,
  onBack, 
  onUpload, 
  isLoading 
}) => {
  const [editingJob, setEditingJob] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all') // 'all', 'needsReview', 'complete'

  const allSelected = jobs.length > 0 && jobs.every(job => job.isSelected)
  const someSelected = jobs.some(job => job.isSelected)

  const handleEditJob = (job) => {
    setEditingJob(job)
  }

  const handleCloseEdit = () => {
    setEditingJob(null)
  }

  const handleSaveJob = (updatedJob) => {
    onJobUpdate(updatedJob)
    setEditingJob(null)
  }

  const formatSalary = (job) => {
    if (!job.salaryMin && !job.salaryMax) return 'Not specified'
    if (job.salaryMin && job.salaryMax) {
      return `$${job.salaryMin.toLocaleString()} - $${job.salaryMax.toLocaleString()}`
    }
    if (job.salaryMin) return `From $${job.salaryMin.toLocaleString()}`
    if (job.salaryMax) return `Up to $${job.salaryMax.toLocaleString()}`
    return 'Not specified'
  }

  const getCandidateTypeName = (candidateTypeId) => {
    const types = { 1: 'Physician', 2: 'Nurse Practitioner', 3: 'Physician Assistant' }
    return types[candidateTypeId] || 'Not specified'
  }

  const getSpecialtyNames = (specialtyIds) => {
    const specialties = { 
      1: 'Internal Medicine', 
      2: 'Emergency Medicine', 
      3: 'Family Medicine', 
      4: 'Cardiology', 
      5: 'Pediatrics', 
      6: 'Surgery' 
    }
    if (!specialtyIds || specialtyIds.length === 0) return 'Not specified'
    return specialtyIds.map(id => specialties[id] || `Specialty ${id}`).join(', ')
  }

  const getSubspecialtyNames = (subspecialtyIds) => {
    const subspecialties = { 
      1: 'Interventional Cardiology', 
      2: 'Critical Care', 
      3: 'Gastroenterology', 
      4: 'Oncology' 
    }
    if (!subspecialtyIds || subspecialtyIds.length === 0) return 'Not specified'
    return subspecialtyIds.map(id => subspecialties[id] || `Subspecialty ${id}`).join(', ')
  }

  const getVisaNames = (visaIds) => {
    const visas = { 1: 'H1B', 2: 'J1', 3: 'O-1', 4: 'TN' }
    if (!visaIds || visaIds.length === 0) return 'Not specified'
    return visaIds.map(id => visas[id] || `Visa ${id}`).join(', ')
  }

  // Filter and search jobs
  const filteredJobs = jobs.filter(job => {
    // Status filter
    if (statusFilter === 'needsReview' && !job.needsReview) return false
    if (statusFilter === 'complete' && job.needsReview) return false
    
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase()
      const searchableText = [
        job.title,
        getCandidateTypeName(job.candidateTypeId),
        job.city,
        job.state,
        job.practiceType,
        getSpecialtyNames(job.specialtyIds)
      ].filter(Boolean).join(' ').toLowerCase()
      
      if (!searchableText.includes(searchLower)) return false
    }
    
    return true
  })

  const needsReviewCount = jobs.filter(job => job.needsReview).length
  const completeCount = jobs.length - needsReviewCount

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
        <CircularProgress size={40} sx={{ mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Uploading Selected Jobs...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please wait while we process your job postings.
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WorkIcon color="primary" />
          Review Extracted Jobs ({jobs.length} found)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Review and edit the extracted job postings. Select which ones you'd like to upload.
        </Typography>

        {/* Search and Filter Controls */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search jobs by title, location, specialty..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterListIcon color="action" />
              <ToggleButtonGroup
                value={statusFilter}
                exclusive
                onChange={(e, newFilter) => newFilter && setStatusFilter(newFilter)}
                size="small"
              >
                <ToggleButton value="all">
                  All ({jobs.length})
                </ToggleButton>
                <ToggleButton value="needsReview" sx={{ color: 'warning.main' }}>
                  Needs Review ({needsReviewCount})
                </ToggleButton>
                <ToggleButton value="complete" sx={{ color: 'success.main' }}>
                  Complete ({completeCount})
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Grid>
        </Grid>

        {/* Results summary */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Showing {filteredJobs.length} of {jobs.length} jobs
          {needsReviewCount > 0 && (
            <Chip 
              label={`${needsReviewCount} need attention`} 
              color="warning" 
              size="small" 
              sx={{ ml: 2 }}
            />
          )}
        </Typography>
      </Box>

      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected && !allSelected}
              onChange={(e) => onBulkSelection(e.target.checked)}
            />
          }
          label="Select All Jobs"
        />
        <Chip 
          label={`${selectedJobs.length} selected`} 
          color={selectedJobs.length > 0 ? 'primary' : 'default'} 
          size="small" 
        />
      </Box>

      <Stack spacing={3} sx={{ mb: 4 }}>
        {filteredJobs.map((job) => (
          <Card
            key={job.id}
            sx={{
              border: '1px solid',
              borderColor: job.needsReview 
                ? 'warning.main' 
                : job.isSelected 
                  ? 'primary.main' 
                  : 'divider',
              backgroundColor: job.needsReview 
                ? 'warning.50' 
                : job.isSelected 
                  ? 'primary.50' 
                  : 'background.paper',
              transition: 'all 0.2s ease-in-out',
              boxShadow: job.needsReview ? 2 : 1
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={job.isSelected}
                        onChange={(e) => onJobSelection(job.id, e.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="h6" component="div">
                        {job.title}
                      </Typography>
                    }
                  />
                  {job.needsReview && (
                    <Chip 
                      label="⚠️ Needs Review" 
                      color="warning" 
                      size="small" 
                      variant="filled"
                      sx={{ 
                        ml: 1,
                        fontWeight: 'bold',
                        animation: 'pulse 2s infinite'
                      }}
                    />
                  )}
                </Box>
                <Button
                  startIcon={<EditIcon />}
                  onClick={() => handleEditJob(job)}
                  size="small"
                  variant="outlined"
                >
                  Edit
                </Button>
              </Box>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Candidate Type:</strong> {getCandidateTypeName(job.candidateTypeId)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Location:</strong> {job.city && job.state ? `${job.city}, ${job.state}` : 'Not specified'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Salary:</strong> {formatSalary(job)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Practice Type:</strong> {job.practiceType || 'Not specified'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Salary Type:</strong> {job.salaryType || 'Not specified'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Area:</strong> {job.area || 'Not specified'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Academic Type:</strong> {job.academicType || 'Not specified'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Specialties:</strong> {getSpecialtyNames(job.specialtyIds)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Subspecialties:</strong> {getSubspecialtyNames(job.subspecialtyIds)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Visa Sponsorship:</strong> {getVisaNames(job.visaIds)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Loan Forgiveness:</strong> {job.loanForgiveness || 'Not specified'}
                  </Typography>
                </Grid>
                {job.candidateTypeId === 2 && job.shiftType && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Shift Type:</strong> {job.shiftType}
                    </Typography>
                  </Grid>
                )}
                {job.link && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>External Link:</strong> <a href={job.link} target="_blank" rel="noopener noreferrer">{job.link}</a>
                    </Typography>
                  </Grid>
                )}
              </Grid>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Description:</strong>
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {job.about}
              </Typography>

              <Divider sx={{ my: 2 }} />
              
              <Typography variant="caption" color="text.secondary">
                <strong>Source:</strong> {job.sourceUrl}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {jobs.length === 0 && (
        <Alert severity="warning" sx={{ mb: 4 }}>
          No jobs were found in the provided URLs. Please check the URLs and try again.
        </Alert>
      )}

      {jobs.length > 0 && filteredJobs.length === 0 && (
        <Alert severity="info" sx={{ mb: 4 }}>
          No jobs match your current search or filter criteria. Try adjusting your search terms or selecting a different filter.
        </Alert>
      )}

      {/* Fixed Bottom Action Buttons */}
      <Box sx={{ 
        position: 'sticky',
        bottom: 0,
        left: 0,
        right: 0,
        p: 3,
        borderTop: '1px solid rgba(0, 0, 0, 0.12)',
        backgroundColor: 'background.paper',
        display: 'flex', 
        justifyContent: 'space-between',
        gap: 2,
        mt: 4
      }}>
        <Button
          onClick={onBack}
          disabled={isLoading}
          variant="outlined"
          size="large"
          startIcon={<ArrowBackIcon />}
        >
          Back to URLs
        </Button>
        
        <Button
          onClick={onUpload}
          disabled={isLoading || selectedJobs.length === 0}
          variant="contained"
          size="large"
          startIcon={isLoading && <CircularProgress size={20} />}
        >
          {isLoading ? 'Uploading...' : `Upload ${selectedJobs.length} Job${selectedJobs.length !== 1 ? 's' : ''}`}
        </Button>
      </Box>

      <JobEditModal
        open={!!editingJob}
        job={editingJob}
        onClose={handleCloseEdit}
        onSave={handleSaveJob}
      />
    </Box>
  )
}

export default JobReviewStep
